import React, {useEffect} from 'react';

import {Controller, useFieldArray, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {CreateFactory, CreateFactoryLine} from 'types/profile';

import {
    AddMoreButton,
    ControlContainer,
    FactoryLinesContainer,
    Field,
    LineContainer,
} from './styled';

interface Props {
    factoryIndex: number;
    formData: UseFormMethods<{factories: CreateFactory[]}>;
}

export const FactoryLines: React.FC<Props> = ({factoryIndex, formData}) => {
    const {t} = useTranslation();
    const {fields, append, remove} = useFieldArray<CreateFactoryLine>({
        control: formData.control,
        name: `factories.${factoryIndex}.factoryLines`,
    });

    useEffect(() => {
        if (!fields.length) addLine();
    }, [fields, append]);

    useEffect(() => {
        fields.forEach((_, index) => {
            formData.register(`factories.${factoryIndex}.factoryLines.${index}.name`);
            formData.register(`factories.${factoryIndex}.factoryLines.${index}.code`);
        });
    }, [fields, formData.register]);

    const addLine = () => {
        append({
            customId: uuidv4(),
            name: '',
        });
    };

    const removeLine = (index: number) => {
        if (fields.length > 1) {
            remove(index);
        }
    };

    return (
        <>
            <FactoryLinesContainer>
                {fields.map((line, lineIndex) => (
                    <LineContainer key={line.customId}>
                        <Field>
                            <Controller
                                defaultValue={line.name}
                                name={`factories.${factoryIndex}.factoryLines.${lineIndex}.name`}
                                control={formData.control}
                                render={({onChange, onBlur, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        placeholder={`${t(
                                            'pages.profile.createFactory.line',
                                        )} ${lineIndex + 1}`}
                                    />
                                )}
                            />
                        </Field>
                        <ControlContainer
                            disabled={fields.length <= 1}
                            onClick={() => removeLine(lineIndex)}
                        >
                            <Icon source={CrossIcon} width={16} height={16} />
                        </ControlContainer>
                    </LineContainer>
                ))}
            </FactoryLinesContainer>
            <AddMoreButton onClick={addLine} variant={ButtonVariant.Text} type="button">
                + {t('pages.profile.createFactory.addLine')}
            </AddMoreButton>
        </>
    );
};
